import logger from '../../lib/logger';
import React, { useEffect, useRef, useState } from 'react';
import { tokenStore } from '../../lib/tokenStore';
import { 
  Box, 
  Circle, 
  Square, 
  Cylinder, 
  Projector,
  Plus,
  Upload,
  File as FileIcon,
  Trash2,
  ChevronDown,
  ChevronUp,
  Settings,
  X,
  Edit,
  MoreVertical,
  Frame,
  Anchor,
  Download,
  FolderOpen,
  Folder,
  FolderPlus,
  Monitor,
  Triangle,
  RotateCcw,
  Columns,
  Grip,
  Check,
  Palette,
  Home,
  ArrowRight,
  Wand2,
  PersonStanding,
  Search,
  X as XIcon
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { useApp } from '../../context/AppContext';
import { projectorsApi, filesApi, modelFoldersApi } from '../../lib/api';
import { convertFileToGLB, MAX_UPLOAD_BYTES, getExt } from '../../lib/glbConverter';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import ContentPanel from './ContentPanel';
import LedTestPattern from './LedTestPattern';

// Walk every projector spec list inside `library` (both the dynamic `brands`
// dict and legacy top-level slugs like `barco`/`panasonic`/`custom`). Returns
// a NEW library object with `mapper(spec)` applied to every matching spec.
const mapLibrarySpecs = (library, mapper) => {
  const next = { ...library };
  if (next.brands && typeof next.brands === 'object') {
    const brands = {};
    for (const [b, specs] of Object.entries(next.brands)) {
      brands[b] = (specs || []).map(mapper);
    }
    next.brands = brands;
  }
  for (const k of Object.keys(next)) {
    if (k === 'brands' || k === 'test_patterns') continue;
    if (Array.isArray(next[k])) next[k] = next[k].map(mapper);
  }
  return next;
};

const LibraryPanel = ({ onClose }) => {
  const { 
    projectorLibrary, 
    setProjectorLibrary,
    uploadedFiles,
    setUploadedFiles,
    addProjector,
    addSceneObject,
    activeLeftTab,
    setActiveLeftTab,
    openAddProjectorDialog
  } = useApp();
  
  const fileInputRef = useRef(null);
  const convertInputRef = useRef(null); // Upload & Convert flow
  const [expandedProjector, setExpandedProjector] = useState(null);
  const [customProjectorOpen, setCustomProjectorOpen] = useState(false);

  // Projector library search — filters Barco / Panasonic / Custom rows
  // by brand, model, ID, or any token in the spec name. Empty string =
  // show everything (current behavior).
  const [projectorSearch, setProjectorSearch] = useState('');
  
  // Edit Projector Dialog state
  const [editProjectorOpen, setEditProjectorOpen] = useState(false);
  const [editingSpec, setEditingSpec] = useState(null);
  
  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportingFile, setExportingFile] = useState(null);
  const [exportFormat, setExportFormat] = useState('obj');
  
  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [convertLabel, setConvertLabel] = useState('');

  // 3D Model Folders state
  const FOLDER_COLOR_SWATCHES = ['#a855f7', '#3b82f6', '#22c55e', '#eab308', '#ef4444', '#ec4899', '#06b6d4', '#f97316'];
  const [modelFolders, setModelFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null); // null = root / all
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState(FOLDER_COLOR_SWATCHES[0]);
  const [editingFolder, setEditingFolder] = useState(null); // { id, name, color }
  const [editFolderDialogOpen, setEditFolderDialogOpen] = useState(false);
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [deleteFolderDialogOpen, setDeleteFolderDialogOpen] = useState(false);
  
  const [customProjector, setCustomProjector] = useState({
    brand: 'Custom',
    model: '',
    brightness: 10000,
    resolution_width: 1920,
    resolution_height: 1200,
    aspect_ratio: '16:10',
    throw_ratio_min: 1.0,
    throw_ratio_max: 2.0,
    lens_shift_h: 30,
    lens_shift_v: 60,
    weight: 20,
    width: 500,
    height: 200,
    depth: 600,
    frame_width: 600,
    frame_depth: 700,
    frame_height: 300,
    frame_weight: 5
  });
  const [newLens, setNewLens] = useState({ name: '', throw_ratio_min: '', throw_ratio_max: '', lens_shift_h: 30, lens_shift_v: 60, weight: 0 });
  
  // Editing lens state for inline edit in Manage Lenses
  const [editingLensId, setEditingLensId] = useState(null);
  const [editingLensData, setEditingLensData] = useState(null);

  const loadProjectorLibrary = async () => {
    try {
      const response = await projectorsApi.getLibrary();
      setProjectorLibrary(response.data);
    } catch (error) {
      logger.error('Failed to load projector library:', error);
    }
  };

  const loadFiles = async () => {
    try {
      const response = await filesApi.getAll();
      setUploadedFiles(response.data);
    } catch (error) {
      logger.error('Failed to load files:', error);
    }
  };

  // ---------- 3D Model Folder handlers ----------
  const loadModelFolders = async () => {
    try {
      const response = await modelFoldersApi.getAll();
      setModelFolders(response.data || []);
    } catch (error) {
      logger.error('Failed to load model folders:', error);
    }
  };

  const handleCreateFolder = async () => {
    const name = (newFolderName || '').trim();
    if (!name) { toast.error('Enter a folder name'); return; }
    try {
      const res = await modelFoldersApi.create(name, newFolderColor);
      setModelFolders(prev => [...prev, res.data]);
      setNewFolderDialogOpen(false);
      setNewFolderName('');
      setNewFolderColor(FOLDER_COLOR_SWATCHES[0]);
      toast.success(`Folder "${res.data.name}" created`);
    } catch (e) {
      toast.error('Failed to create folder');
    }
  };

  const openEditFolder = (folder) => {
    setEditingFolder({ ...folder });
    setEditFolderDialogOpen(true);
  };

  const handleSaveFolderEdit = async () => {
    if (!editingFolder) return;
    const name = (editingFolder.name || '').trim();
    if (!name) { toast.error('Folder name cannot be empty'); return; }
    try {
      const res = await modelFoldersApi.update(editingFolder.id, {
        name,
        color: editingFolder.color
      });
      setModelFolders(prev => prev.map(f => f.id === editingFolder.id ? { ...f, ...res.data } : f));
      setEditFolderDialogOpen(false);
      setEditingFolder(null);
      toast.success('Folder updated');
    } catch (e) {
      toast.error('Failed to update folder');
    }
  };

  const openDeleteFolder = (folder) => {
    setDeletingFolder(folder);
    setDeleteFolderDialogOpen(true);
  };

  const handleDeleteFolder = async () => {
    if (!deletingFolder) return;
    try {
      const res = await modelFoldersApi.delete(deletingFolder.id);
      const deletedFiles = res.data?.deleted_files || 0;
      setModelFolders(prev => prev.filter(f => f.id !== deletingFolder.id));
      setUploadedFiles(prev => prev.filter(f => f.folder_id !== deletingFolder.id));
      if (activeFolderId === deletingFolder.id) setActiveFolderId(null);
      setDeleteFolderDialogOpen(false);
      setDeletingFolder(null);
      toast.success(`Folder deleted${deletedFiles ? ` (${deletedFiles} file${deletedFiles === 1 ? '' : 's'} removed)` : ''}`);
    } catch (e) {
      toast.error('Failed to delete folder');
    }
  };

  const handleMoveFileToFolder = async (fileId, folderId) => {
    try {
      const res = await filesApi.move(fileId, folderId);
      setUploadedFiles(prev => prev.map(f => f.id === fileId ? { ...f, folder_id: res.data.folder_id } : f));
      const target = folderId ? modelFolders.find(f => f.id === folderId) : null;
      toast.success(target ? `Moved to "${target.name}"` : 'Moved to root');
    } catch (e) {
      toast.error('Failed to move file');
    }
  };

  // Handle file export
  const handleExportFile = async () => {
    if (!exportingFile) return;
    
    try {
      // Use the API endpoint to serve files (avoids ingress routing issues)
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      // Use file_id if available, otherwise try direct file_path
      const downloadUrl = exportingFile.id 
        ? `${backendUrl}/api/files/serve/${exportingFile.id}`
        : `${backendUrl}${exportingFile.file_path}`;
      
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Failed to fetch file');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      // Create download link with correct extension
      const a = document.createElement('a');
      a.href = url;
      // Use the original extension from the file
      const originalExt = exportingFile.file_type || 'obj';
      a.download = `${exportingFile.name}.${originalExt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${exportingFile.name}.${originalExt}`);
      setExportDialogOpen(false);
      setExportingFile(null);
    } catch (error) {
      logger.error('Export failed:', error);
      toast.error('Export failed');
    }
  };

  // Open export dialog
  const openExportDialog = (file) => {
    setExportingFile(file);
    setExportFormat(file.file_type || 'obj');
    setExportDialogOpen(true);
  };

  useEffect(() => {
    loadProjectorLibrary();
    loadFiles();
    loadModelFolders();
  }, []);

  // Open edit projector dialog
  const openEditProjector = (spec, category) => {
    setEditingSpec({ ...spec, category });
    setEditProjectorOpen(true);
  };

  // Save edited projector
  const handleSaveEditedProjector = () => {
    if (!editingSpec) return;
    
    const { category, ...specData } = editingSpec;
    
    setProjectorLibrary(prev => {
      const updated = { ...prev };
      if (updated[category]) {
        updated[category] = updated[category].map(spec => 
          spec.id === specData.id ? specData : spec
        );
      }
      return updated;
    });
    
    setEditProjectorOpen(false);
    toast.success(`Updated ${editingSpec.model}`);
  };

  // Delete projector from library
  const handleDeleteProjector = async (specId, category) => {
    setProjectorLibrary(prev => {
      const updated = { ...prev };
      if (updated[category]) {
        updated[category] = updated[category].filter(spec => spec.id !== specId);
      }
      return updated;
    });
    // For custom projectors, also delete from the backend so it doesn't reappear on reload.
    if (category === 'custom') {
      try {
        await projectorsApi.deleteCustom(specId);
      } catch (error) {
        logger.warn('Failed to delete custom projector from backend:', error);
      }
    } else {
      // Built-in projector: user is hiding it locally only (no-op on backend)
    }
    toast.success('Projector removed from library');
  };

  const handleAddProjector = (spec, withFrame = false) => {
    const projector = addProjector(spec);
    if (withFrame && projector) {
      // Update the newly added projector to have a frame
      setTimeout(() => {
        const { updateProjector } = useApp.getState?.() || {};
        // We need to use context differently - this is handled in the addProjector
      }, 0);
    }
    toast.success(`Added ${spec.brand} ${spec.model}${withFrame ? ' with frame' : ''}`);
  };

  const handleAddProjectorWithFrame = (spec) => {
    // Pass a modified spec that includes frame option
    const specWithFrame = { ...spec, showFrame: true };
    addProjector(specWithFrame);
    toast.success(`Added ${spec.brand} ${spec.model} with frame`);
  };

  const handleAddCustomProjector = async () => {
    if (!customProjector.model) {
      toast.error('Please enter a model name');
      return;
    }

    const localId = `custom-${uuidv4()}`;
    const spec = {
      ...customProjector,
      id: localId,
      lenses: [
        {
          id: 'default',
          name: 'Default',
          throw_ratio_min: customProjector.throw_ratio_min,
          throw_ratio_max: customProjector.throw_ratio_max,
          lens_shift_h: customProjector.lens_shift_h,
          lens_shift_v: customProjector.lens_shift_v,
          weight: 0
        }
      ]
    };

    // Persist to backend (per-user). Fall back to local-only if offline.
    let savedSpec = spec;
    try {
      const response = await projectorsApi.createCustom(spec);
      if (response?.data) savedSpec = { ...spec, ...response.data };
    } catch (error) {
      logger.warn('Failed to persist custom projector, using local-only copy:', error);
    }

    // Add to custom library
    setProjectorLibrary(prev => ({
      ...prev,
      custom: [...(prev.custom || []), savedSpec]
    }));

    // Add to scene
    addProjector(savedSpec);
    setCustomProjectorOpen(false);
    toast.success(`Added custom projector: ${savedSpec.model}`);
  };

  // Centralised helper — persist the current lens list of a spec to the backend.
  // Used by every lens add / edit / delete action so the user's library survives
  // refresh, re-login, and new projects.
  const persistSpecLenses = async (specId, nextLenses) => {
    try {
      await projectorsApi.saveSpecLenses(specId, nextLenses);
    } catch (error) {
      logger.warn('Failed to save lens changes to user library:', error);
    }
  };

  const handleAddLensToSpec = (specId) => {
    if (!newLens.name || !newLens.throw_ratio_min || !newLens.throw_ratio_max) {
      toast.error('Please fill Model No and Ratio fields');
      return;
    }

    const lens = {
      id: uuidv4(),
      name: newLens.name,
      throw_ratio_min: parseFloat(newLens.throw_ratio_min),
      throw_ratio_max: parseFloat(newLens.throw_ratio_max),
      lens_shift_h: parseInt(newLens.lens_shift_h) || 0,
      lens_shift_v: parseInt(newLens.lens_shift_v) || 0,
      weight: parseFloat(newLens.weight) || 0
    };

    // Update projector library (local state) AND persist full lens list to backend.
    setProjectorLibrary(prev => mapLibrarySpecs(prev, (spec) => {
      if (spec.id !== specId) return spec;
      const nextLenses = [...(spec.lenses || []), lens];
      // Fire-and-forget persistence (per-user, auto-saved)
      persistSpecLenses(specId, nextLenses);
      return { ...spec, lenses: nextLenses };
    }));

    setNewLens({ name: '', throw_ratio_min: '', throw_ratio_max: '', lens_shift_h: 30, lens_shift_v: 60, weight: 0 });
    toast.success('Lens added');
  };

  // Start editing a lens
  const handleStartEditLens = (lens) => {
    setEditingLensId(lens.id);
    setEditingLensData({ ...lens });
  };

  // Save edited lens
  const handleSaveEditLens = (specId) => {
    if (!editingLensData || !editingLensData.name) return;

    setProjectorLibrary(prev => mapLibrarySpecs(prev, (spec) => {
      if (spec.id !== specId || !spec.lenses) return spec;
      const nextLenses = spec.lenses.map(l => l.id === editingLensId ? {
        ...l,
        name: editingLensData.name,
        throw_ratio_min: parseFloat(editingLensData.throw_ratio_min) || l.throw_ratio_min,
        throw_ratio_max: parseFloat(editingLensData.throw_ratio_max) || l.throw_ratio_max,
        lens_shift_h: parseInt(editingLensData.lens_shift_h) || 0,
        lens_shift_v: parseInt(editingLensData.lens_shift_v) || 0,
        weight: parseFloat(editingLensData.weight) || 0
      } : l);
      persistSpecLenses(specId, nextLenses);
      return { ...spec, lenses: nextLenses };
    }));

    setEditingLensId(null);
    setEditingLensData(null);
    toast.success('Lens updated');
  };

  const handleDeleteLens = (specId, lensId) => {
    setProjectorLibrary(prev => mapLibrarySpecs(prev, (spec) => {
      if (spec.id !== specId || !spec.lenses) return spec;
      const nextLenses = spec.lenses.filter(l => l.id !== lensId);
      persistSpecLenses(specId, nextLenses);
      return { ...spec, lenses: nextLenses };
    }));
    toast.success('Lens removed');
  };

  const handleAddPrimitive = (type) => {
    // Built-in static 3D models under /public/models. Routed through the
    // standard mesh-loading path so projection, transforms, PDF export
    // all work without any special-casing downstream.
    // Real-world heights are baked directly into the OBJ vertex data so the
    // models arrive at the documented size (Man ~1.75m, Woman ~1.65m) with
    // a clean scale of 1 in the Properties panel.
    const BUILTIN_3D_MODELS = {
      builtin_human_man:   { name: 'Human Man',   file: 'human-man.obj' },
      builtin_human_woman: { name: 'Human Woman', file: 'human-woman.obj' },
    };
    if (BUILTIN_3D_MODELS[type]) {
      const m = BUILTIN_3D_MODELS[type];
      addSceneObject({
        name: m.name,
        type: 'mesh',
        file_path: '/models/' + m.file,
        file_name: m.file,
        file_ext: 'obj',
        position: { x: 0, y: 0, z: 0 },
        // Human OBJs were exported facing -Z (away from the default front
        // camera). Spawn them at 180° on Y so they face the user out of the
        // box — much more natural as a scale reference than seeing the back
        // of the head.
        rotation: { x: 0, y: 180, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        color: '#888888',
        // Lowpoly humans look much cleaner without polygon edges drawn —
        // the silhouette is still distinct enough as a scale reference.
        show_edges: false,
      });
      toast.success('Added ' + m.name);
      return;
    }

    // LED Screen has special handling
    if (type === 'led_screen') {
      addSceneObject({
        name: 'LED Screen',
        type: 'led_screen',
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        color: '#111111',
        ledConfig: {
          ledMode: 'standard',
          tileWidth: 500,
          tileHeight: 500,
          tileDepth: 80,
          tileResH: 128,
          tileResV: 128,
          pixelPitch: 3.9,
          columns: 1,
          rows: 1,
          powerPerTile: 200,
          maxPowerPerTile: 250,
          curveAngle: 0,
          cabinetStep: 500,
          cornerAngle: 90,
          smoothBend: false,
          contentItemId: null,
          contentUrl: null
        }
      });
      toast.success('Added LED Screen');
      return;
    }

    // Curved LED Wall
    if (type === 'led_curved') {
      addSceneObject({
        name: 'LED Curved',
        type: 'led_screen',
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        color: '#111111',
        ledConfig: {
          ledMode: 'curved',
          tileWidth: 500,
          tileHeight: 500,
          tileDepth: 80,
          tileResH: 128,
          tileResV: 128,
          pixelPitch: 3.9,
          columns: 8,
          rows: 3,
          powerPerTile: 200,
          maxPowerPerTile: 250,
          curveAngle: 5,
          cabinetStep: 500,
          cornerAngle: 90,
          smoothBend: false,
          contentItemId: null,
          contentUrl: null
        }
      });
      toast.success('Added Curved LED Wall');
      return;
    }

    // Corner LED tile
    if (type === 'led_corner') {
      addSceneObject({
        name: 'LED Corner',
        type: 'led_screen',
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        color: '#111111',
        ledConfig: {
          ledMode: 'corner',
          tileWidth: 500,
          tileHeight: 500,
          tileDepth: 80,
          tileResH: 128,
          tileResV: 128,
          pixelPitch: 3.9,
          columns: 4,
          rows: 3,
          leftColumns: 2,
          rightColumns: 2,
          powerPerTile: 200,
          maxPowerPerTile: 250,
          curveAngle: 0,
          cabinetStep: 500,
          cornerAngle: 90,
          smoothBend: false,
          contentItemId: null,
          contentUrl: null
        }
      });
      toast.success('Added LED Corner Tile');
      return;
    }

    // Soft Corner LED tile
    if (type === 'led_soft_corner') {
      addSceneObject({
        name: 'LED Soft Corner',
        type: 'led_screen',
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        color: '#111111',
        ledConfig: {
          ledMode: 'soft_corner',
          tileWidth: 500,
          tileHeight: 500,
          tileDepth: 80,
          tileResH: 128,
          tileResV: 128,
          pixelPitch: 3.9,
          columns: 4,
          rows: 3,
          leftColumns: 2,
          rightColumns: 2,
          powerPerTile: 200,
          maxPowerPerTile: 250,
          curveAngle: 0,
          cabinetStep: 500,
          cornerAngle: -90,
          smoothBend: false,
          contentItemId: null,
          contentUrl: null
        }
      });
      toast.success('Added LED Soft Corner');
      return;
    }

    // Flexible LED
    if (type === 'led_flexible') {
      addSceneObject({
        name: 'LED Flexible',
        type: 'led_screen',
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        color: '#111111',
        ledConfig: {
          ledMode: 'flexible',
          tileWidth: 500,
          tileHeight: 500,
          tileDepth: 40,
          tileResH: 128,
          tileResV: 128,
          pixelPitch: 2.6,
          columns: 8,
          rows: 3,
          powerPerTile: 150,
          maxPowerPerTile: 200,
          curveAngle: 5,
          cabinetStep: 500,
          cornerAngle: 90,
          smoothBend: true,
          contentItemId: null,
          contentUrl: null
        }
      });
      toast.success('Added Flexible LED');
      return;
    }
    
    // Mirror has special handling
    if (type === 'mirror') {
      addSceneObject({
        name: 'Mirror',
        type: 'mirror',
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        dimensions: { width: 2, height: 2, depth: 0 },
        color: '#222222',
        mirrorConfig: {
          reflectionEnabled: false,
          resolution: 512,
          reflectionLevel: 1.0,
        }
      });
      toast.success('Added Mirror');
      return;
    }

    // Scaffolding has special handling
    if (type === 'scaffolding') {
      addSceneObject({
        name: 'Scaffolding',
        type: 'scaffolding',
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
        color: '#888888',
        // Per UX request: scaffolding never shows edge outlines. The Show
        // Edges toggle is also hidden in the properties panel for this type.
        show_edges: false,
        scaffoldConfig: {
          width: 3,
          depth: 2,
          height: 3,
          // Default: deck only on the TOP level. Operators usually want a
          // clean structure they can add decks to as needed — placing a deck
          // on every level was noisy and forced extra clicks to remove them.
          showDecks: [3],
          showStairs: [],
          structureColor: '#C0C0C0',
          // Default deck color: a neutral white-grey that reads cleanly under
          // stage lighting and beam visualisations. Users can recolor in props.
          deckColor: '#D9D9D9',
          snapToGrid: true,
          deckLevels: {},
          stairLevels: {},
          supportScaffolds: [],
          counterweights: [],
          counterweightColor: '#A0A0A0',
          showCWLabels: true,
          materialType: 'aluminium',
          loadInputs: { topLoad: 0, sideLoad: 0, sideLoadSides: {} }
        }
      });
      toast.success('Added Scaffolding');
      return;
    }

    const configs = {
      box: { name: 'Cube', scale: { x: 1, y: 1, z: 1 }, position: { x: 0, y: 0, z: 0 } },
      sphere: { name: 'Sphere', scale: { x: 1, y: 1, z: 1 }, position: { x: 0, y: 0, z: 0 } },
      plane: { name: 'Plane', scale: { x: 1, y: 1, z: 1 }, position: { x: 0, y: 0, z: 0 }, dimensions: { width: 1, height: 1, depth: 0 } },
      cylinder: { name: 'Cylinder', scale: { x: 1, y: 1, z: 1 }, position: { x: 0, y: 0, z: 0 } },
      screen: { name: 'Screen', engineType: 'plane', scale: { x: 1, y: 1, z: 1 }, position: { x: 0, y: 0, z: 0 }, dimensions: { width: 4, height: 2.25, depth: 0 } },
      // 16:10 Screen — distinct UI entry, renders as a flat projection plane
      // via the existing 'plane' engine path (CreatePlane). No engine changes.
      screen_1610: { name: '16:10 Screen', engineType: 'plane', scale: { x: 1, y: 1, z: 1 }, position: { x: 0, y: 0, z: 0 }, dimensions: { width: 4, height: 2.5, depth: 0 } },
      scrim_screen: { name: 'Scrim Screen', scale: { x: 1, y: 1, z: 1 }, position: { x: 0, y: 0, z: 0 }, dimensions: { width: 4, height: 2.25, depth: 0 } },
      dome: { name: 'Dome', scale: { x: 1, y: 1, z: 1 }, position: { x: 0, y: 0, z: 0 } }
    };
    
    const config = configs[type] || configs.box;
    
    addSceneObject({
      name: config.name,
      // Most primitives map their type 1:1 to the engine type. Only `box`
      // (UI label "Cube") and entries that opt in via `engineType` (e.g.
      // 16:10 Screen → 'screen') translate to a different engine type.
      type: type === 'box' ? 'cube' : (config.engineType || type),
      position: config.position,
      rotation: { x: 0, y: 0, z: 0 },
      scale: config.scale,
      ...(config.dimensions ? { dimensions: config.dimensions } : {}),
      color: '#888888'
    });
    
    toast.success(`Added ${config.name}`);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      // Create FormData for upload with progress tracking
      const formData = new FormData();
      formData.append('file', file);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(percentComplete);
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (e) {
              reject(new Error('Invalid response'));
            }
          } else {
            reject(new Error(xhr.statusText || 'Upload failed'));
          }
        });
        
        xhr.addEventListener('error', () => reject(new Error('Network error')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));
        
        xhr.open('POST', `${backendUrl}/api/files/upload`);
        const authToken = tokenStore.get();
        if (authToken) xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
        xhr.send(formData);
      });
      
      const response = await uploadPromise;
      // If a folder is currently active, move the uploaded file into it
      let finalFile = response;
      if (activeFolderId) {
        try {
          const moved = await filesApi.move(response.id, activeFolderId);
          finalFile = moved.data;
        } catch (mvErr) {
          logger.error('Failed to move newly uploaded file into folder:', mvErr);
        }
      }
      setUploadedFiles(prev => [...prev, finalFile]);
      toast.success('File uploaded successfully');
      
      // Show info message for FBX files
      if (response.info) {
        toast.info(response.info, { duration: 5000 });
      }
    } catch (error) {
      logger.error('Upload error:', error);
      toast.error('Failed to upload file: ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
    
    e.target.value = '';
  };

  // ── Upload & Convert (always outputs optimized GLB) ─────────────────
  // Loads OBJ/FBX/GLTF/GLB into a hidden Babylon scene → exports as GLB →
  // captures a thumbnail → uploads BOTH to the user's library. Original
  // file is NOT stored (the GLB is the optimized replacement).
  const handleConvertUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // allow re-picking same file later

    const ext = getExt(file.name);
    if (!['obj', 'fbx', 'gltf', 'glb'].includes(ext)) {
      toast.error(`Unsupported format .${ext}. Use OBJ, FBX, GLTF or GLB.`);
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB, max 200 MB)`);
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.warning(`Large file (${(file.size / 1024 / 1024).toFixed(0)} MB) — conversion may take a while.`,
        { duration: 4000 });
    }

    setIsConverting(true);
    setConvertLabel('Loading…');

    try {
      // Step 1: client-side conversion + thumbnail
      const { glbBlob, thumbnailBlob, originalFormat, baseName } = await convertFileToGLB(file, {
        onProgress: (pct, label) => {
          setUploadProgress(pct);
          if (label) setConvertLabel(label);
        },
      });

      // Step 2: upload the optimized GLB via the existing files endpoint
      setConvertLabel('Uploading optimized GLB…');
      const glbFile = new File([glbBlob], `${baseName}.glb`, { type: 'model/gltf-binary' });
      const uploadResp = await filesApi.upload(glbFile);
      let finalFile = uploadResp.data;

      // Step 3: optional — upload the thumbnail (best effort, never blocks)
      if (thumbnailBlob && finalFile?.id) {
        try {
          const { contentApi } = await import('../../lib/api');
          const thumbFile = new File([thumbnailBlob], `${baseName}-thumb.jpg`, { type: 'image/jpeg' });
          await contentApi.upload(thumbFile);
          // Note: linking the thumbnail to the model is a UI concern —
          // future enhancement can write the thumbnail's content/serve URL
          // into the file metadata. For now the thumbnail is just stored.
        } catch (thumbErr) {
          logger.warn('Thumbnail upload skipped:', thumbErr?.message);
        }
      }

      // Step 4: move into active folder if any
      if (activeFolderId && finalFile?.id) {
        try {
          const moved = await filesApi.move(finalFile.id, activeFolderId);
          finalFile = moved.data;
        } catch (mvErr) {
          logger.warn('Move into folder failed:', mvErr?.message);
        }
      }

      setUploadedFiles((prev) => [...prev, finalFile]);
      const sizeKB = (glbBlob.size / 1024).toFixed(0);
      toast.success(
        originalFormat === 'glb'
          ? `Optimized GLB stored (${sizeKB} KB)`
          : `Converted .${originalFormat} → GLB (${sizeKB} KB)`,
        { duration: 4000 }
      );
    } catch (err) {
      logger.error('Convert upload error:', err);
      toast.error(`Conversion failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsConverting(false);
      setUploadProgress(0);
      setConvertLabel('');
    }
  };

  const handleFileDelete = async (fileId) => {
    try {
      await filesApi.delete(fileId);
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
      toast.success('File deleted');
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleAddMeshToScene = (file) => {
    // Show loading toast for large files
    const fileSizeMB = file.file_size / (1024 * 1024);
    let loadingToastId;
    
    if (fileSizeMB > 10) {
      loadingToastId = toast.loading(`Loading ${file.name} (${fileSizeMB.toFixed(1)}MB)... This may take a moment.`, {
        duration: Infinity
      });
    }
    
    // Warn about very large files
    if (fileSizeMB > 50) {
      toast.warning('Large file detected. Browser may slow down.', { duration: 5000 });
    }
    
    addSceneObject({
      name: file.name,
      type: 'mesh',
      file_id: file.id,
      file_path: file.file_path,  // Store relative path, BabylonScene will construct full URL
      file_name: file.original_filename,
      file_ext: file.file_type,
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      color: '#888888',
      _loadingToastId: loadingToastId // Pass to dismiss later
    });
    
    // The success toast will be shown from BabylonScene when loaded
    if (!loadingToastId) {
      toast.success(`Added ${file.name} to scene`);
    }
  };

  const primitives = [
    { type: 'box', icon: Box, label: 'Cube', description: 'Basic 3D box' },
    { type: 'sphere', icon: Circle, label: 'Sphere', description: 'Basic sphere' },
    { type: 'plane', icon: Square, label: 'Plane', description: 'Flat surface' },
    { type: 'cylinder', icon: Cylinder, label: 'Cylinder', description: 'Basic cylinder' },
    { type: 'screen', icon: Square, label: 'Screen', description: 'Projection surface' },
    { type: 'screen_1610', icon: Square, label: '16:10 Screen', description: '16:10 projection surface' },
    { type: 'scrim_screen', icon: Square, label: 'Scrim Screen', description: 'Semi-transparent screen' },
    { type: 'dome', icon: Circle, label: 'Dome', description: 'Dome structure' },
    { type: 'mirror', icon: Square, label: 'Mirror', description: 'Planar reflection surface' },
    // Built-in 3D model (static asset under /public/models). Loaded via the
    // standard `type: 'mesh'` path so projection-receiver, transforms and
    // PDF-export work out of the box.
    { type: 'builtin_human_man', icon: PersonStanding, label: 'Human Man', description: 'Lowpoly male, ~1.7m tall' },
    { type: 'builtin_human_woman', icon: PersonStanding, label: 'Human Woman', description: 'Lowpoly female, ~1.65m tall' }
  ];

  // LED-screen variants — surfaced in their own "LED" library tab.
  const ledScreens = [
    { type: 'led_screen', icon: Monitor, label: 'LED Screen', description: 'Standard LED wall' },
    { type: 'led_curved', icon: Monitor, label: 'LED Curved', description: 'Curved LED wall' },
    { type: 'led_corner', icon: Monitor, label: 'LED Corner', description: 'Sharp angle LED' },
    { type: 'led_soft_corner', icon: Monitor, label: 'LED Soft Corner', description: 'Smooth corner LED' },
    { type: 'led_flexible', icon: Monitor, label: 'LED Flexible', description: 'Smooth curved LED' }
  ];

  // Truss structure types for the Truss tab
  const trussStructures = [
    { 
      structureType: 'straight', 
      icon: Columns, 
      label: 'Straight Truss', 
      description: 'Standard straight segment',
      defaultConfig: { structureType: 'straight', trussProfile: 'F34', length: 3, color: '#C0C0C0', showBasePlates: false }
    },
    { 
      structureType: 'goalpost', 
      icon: Frame, 
      label: 'Goal Post', 
      description: '2 towers + top truss',
      defaultConfig: { 
        structureType: 'goalpost', trussProfile: 'F34', color: '#C0C0C0', showBasePlates: true,
        goalpost: { heightLeft: 5, heightRight: 5, topWidth: 6 }, junctionType: '2way'
      }
    },
    { 
      structureType: 'box_system', 
      icon: Grip, 
      label: 'Box System', 
      description: 'Ground support grid',
      defaultConfig: { 
        structureType: 'box_system', trussProfile: 'F34', color: '#C0C0C0', showBasePlates: true,
        boxSystem: { width: 8, depth: 6, height: 6, pillarMode: 'auto', pillarCount: 4, liftMode: 'manual', liftHeight: 0, shiftX: 0, topSupport: false },
        junctionType: '4way'
      }
    },
    { 
      structureType: 'circular', 
      icon: RotateCcw, 
      label: 'Circular Truss', 
      description: 'Ring / Arc truss',
      defaultConfig: { 
        structureType: 'circular', trussProfile: 'F34', color: '#C0C0C0',
        circular: { radius: 3, segments: 12, arcAngle: 360 }
      }
    },
    { 
      structureType: 'angle', 
      icon: Triangle, 
      label: 'Angle Truss', 
      description: 'Adjustable angle joint',
      defaultConfig: { 
        structureType: 'angle', trussProfile: 'F34', color: '#C0C0C0',
        angle: { length1: 2, length2: 2, angle: 90, direction: 'horizontal' }, junctionType: '2way'
      }
    }
  ];

  const handleAddTruss = (trussItem) => {
    addSceneObject({
      name: trussItem.label,
      type: 'truss_system',
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      color: '#C0C0C0',
      trussConfig: { ...trussItem.defaultConfig }
    });
    toast.success(`Added ${trussItem.label}`);
  };

  // ProjectorCard is intentionally a nested component because it uses many parent closures
  // eslint-disable-next-line react/no-unstable-nested-components
  const ProjectorCard = ({ spec, category = 'barco' }) => {
    const isExpanded = expandedProjector === spec.id;
    const isCustom = category === 'custom';
    
    return (
      <div className="projector-card-container">
        <div 
          className="projector-card"
          data-testid={`projector-${spec.id}`}
        >
          <div className="flex items-start justify-between">
            <div 
              className="flex-1 cursor-pointer" 
              onClick={(e) => { e.stopPropagation(); openAddProjectorDialog(spec); }}
            >
              <div className="projector-card-title">{spec.model}</div>
              <div className="projector-card-subtitle">{spec.brand}</div>
            </div>
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Projector className="w-5 h-5 text-fuchsia-400" />
              {/* Edit/Delete dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => e.stopPropagation()}>
                    <MoreVertical className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); openEditProjector(spec, category); }}
                    className="text-xs"
                  >
                    <Edit className="w-3 h-3 mr-2" />
                    Edit Projector
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); handleDeleteProjector(spec.id, category); }}
                    className="text-xs text-red-400"
                  >
                    <Trash2 className="w-3 h-3 mr-2" />
                    Delete from Library
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div 
            className="projector-card-specs cursor-pointer" 
            onClick={(e) => { e.stopPropagation(); openAddProjectorDialog(spec); }}
          >
            <span className="projector-spec-badge">{spec.brightness?.toLocaleString()} lm</span>
            <span className="projector-spec-badge">{spec.aspect_ratio}</span>
            <span className="projector-spec-badge">{spec.throw_ratio_min}-{spec.throw_ratio_max}</span>
          </div>
          {spec.weight && (
            <div 
              className="mt-1 text-[10px] text-zinc-500 cursor-pointer" 
              onClick={(e) => { e.stopPropagation(); openAddProjectorDialog(spec); }}
            >
              Size: {spec.width}×{spec.height}×{spec.depth}mm | {spec.weight}kg
            </div>
          )}
          {spec.lenses && (
            <div className="mt-1 text-[10px] text-zinc-500" onClick={(e) => e.stopPropagation()}>
              {spec.lenses.length} lens option{spec.lenses.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        
        {/* Add projector button */}
        <button
          className="w-full text-[10px] text-fuchsia-400 hover:text-fuchsia-300 py-1.5 flex items-center justify-center gap-1 border-t border-zinc-700 bg-fuchsia-500/10 hover:bg-fuchsia-500/20"
          onClick={(e) => { e.stopPropagation(); openAddProjectorDialog(spec); }}
          data-testid={`add-projector-btn-${spec.id}`}
        >
          <Plus className="w-3 h-3" />
          Add to Scene
        </button>
        
        {/* Lens management toggle */}
        <button
          className="w-full text-[10px] text-zinc-500 hover:text-white py-1 flex items-center justify-center gap-1 border-t border-zinc-700"
          onClick={(e) => { e.stopPropagation(); setExpandedProjector(isExpanded ? null : spec.id); }}
        >
          <Settings className="w-3 h-3" />
          Manage Lenses
          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        
        {/* Lens list */}
        {isExpanded && (
          <div className="p-2 bg-zinc-900 border-t border-zinc-700 space-y-2">
            <div className="text-[10px] text-zinc-400 uppercase font-semibold">Lenses</div>
            {spec.lenses?.map(lens => (
              <div key={lens.id} className="bg-zinc-800 rounded overflow-hidden">
                {editingLensId === lens.id ? (
                  /* Edit lens inline form */
                  <div className="p-2 space-y-1.5">
                    <Input
                      value={editingLensData.name}
                      onChange={(e) => setEditingLensData({ ...editingLensData, name: e.target.value })}
                      className="h-7 text-xs"
                      placeholder="Model No"
                    />
                    <div className="grid grid-cols-2 gap-1">
                      <div className="space-y-0.5">
                        <Label className="text-[9px] text-zinc-500">Ratio Min</Label>
                        <Input type="number" step="0.01" value={editingLensData.throw_ratio_min}
                          onChange={(e) => setEditingLensData({ ...editingLensData, throw_ratio_min: e.target.value })}
                          className="h-6 text-xs" />
                      </div>
                      <div className="space-y-0.5">
                        <Label className="text-[9px] text-zinc-500">Ratio Max</Label>
                        <Input type="number" step="0.01" value={editingLensData.throw_ratio_max}
                          onChange={(e) => setEditingLensData({ ...editingLensData, throw_ratio_max: e.target.value })}
                          className="h-6 text-xs" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      <div className="space-y-0.5">
                        <Label className="text-[9px] text-zinc-500">Shift H (%)</Label>
                        <Input type="number" value={editingLensData.lens_shift_h || 0}
                          onChange={(e) => setEditingLensData({ ...editingLensData, lens_shift_h: e.target.value })}
                          className="h-6 text-xs" />
                      </div>
                      <div className="space-y-0.5">
                        <Label className="text-[9px] text-zinc-500">Shift V (%)</Label>
                        <Input type="number" value={editingLensData.lens_shift_v || 0}
                          onChange={(e) => setEditingLensData({ ...editingLensData, lens_shift_v: e.target.value })}
                          className="h-6 text-xs" />
                      </div>
                    </div>
                    <div className="space-y-0.5">
                      <Label className="text-[9px] text-zinc-500">Weight (kg)</Label>
                      <Input type="number" step="0.1" value={editingLensData.weight || 0}
                        onChange={(e) => setEditingLensData({ ...editingLensData, weight: e.target.value })}
                        className="h-6 text-xs" />
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" className="flex-1 h-6 text-[10px]" onClick={() => handleSaveEditLens(spec.id)}>Save</Button>
                      <Button size="sm" variant="outline" className="flex-1 h-6 text-[10px]" onClick={() => { setEditingLensId(null); setEditingLensData(null); }}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  /* Lens display row */
                  <div className="p-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium">{lens.name}</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-zinc-500">{lens.throw_ratio_min}-{lens.throw_ratio_max}</span>
                        <button onClick={(e) => { e.stopPropagation(); handleStartEditLens(lens); }}
                          className="text-zinc-400 hover:text-fuchsia-400" data-testid={`edit-lens-${lens.id}`}>
                          <Edit className="w-3 h-3" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteLens(spec.id, lens.id); }}
                          className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    {(lens.lens_shift_h || lens.lens_shift_v || lens.weight) ? (
                      <div className="text-[9px] text-zinc-500 mt-0.5">
                        {lens.lens_shift_h !== undefined && `H:${lens.lens_shift_h}%`}
                        {lens.lens_shift_v !== undefined && ` V:${lens.lens_shift_v}%`}
                        {lens.weight > 0 && ` | ${lens.weight}kg`}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
            
            {/* Add lens form — all fields */}
            <div className="space-y-1.5 pt-2 border-t border-zinc-700">
              <div className="text-[10px] text-zinc-500">Add New Lens</div>
              <Input
                placeholder="Model No"
                value={newLens.name}
                onChange={(e) => setNewLens({ ...newLens, name: e.target.value })}
                className="h-7 text-xs"
              />
              <div className="grid grid-cols-2 gap-1">
                <div className="space-y-0.5">
                  <Label className="text-[9px] text-zinc-500">Ratio Min</Label>
                  <Input type="number" step="0.01" placeholder="Min"
                    value={newLens.throw_ratio_min}
                    onChange={(e) => setNewLens({ ...newLens, throw_ratio_min: e.target.value })}
                    className="h-6 text-xs" />
                </div>
                <div className="space-y-0.5">
                  <Label className="text-[9px] text-zinc-500">Ratio Max</Label>
                  <Input type="number" step="0.01" placeholder="Max"
                    value={newLens.throw_ratio_max}
                    onChange={(e) => setNewLens({ ...newLens, throw_ratio_max: e.target.value })}
                    className="h-6 text-xs" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1">
                <div className="space-y-0.5">
                  <Label className="text-[9px] text-zinc-500">Shift H (%)</Label>
                  <Input type="number" value={newLens.lens_shift_h}
                    onChange={(e) => setNewLens({ ...newLens, lens_shift_h: e.target.value })}
                    className="h-6 text-xs" />
                </div>
                <div className="space-y-0.5">
                  <Label className="text-[9px] text-zinc-500">Shift V (%)</Label>
                  <Input type="number" value={newLens.lens_shift_v}
                    onChange={(e) => setNewLens({ ...newLens, lens_shift_v: e.target.value })}
                    className="h-6 text-xs" />
                </div>
              </div>
              <div className="space-y-0.5">
                <Label className="text-[9px] text-zinc-500">Weight (kg)</Label>
                <Input type="number" step="0.1" value={newLens.weight}
                  onChange={(e) => setNewLens({ ...newLens, weight: e.target.value })}
                  className="h-6 text-xs" />
              </div>
              <Button
                size="sm"
                className="w-full h-7 text-xs"
                onClick={() => handleAddLensToSpec(spec.id)}
              >
                Add Lens
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="side-panel side-panel-left w-80 flex flex-col" data-testid="library-panel">
      <div className="panel-header flex-shrink-0">
        <h3>Library</h3>
      </div>
      
      <Tabs value={activeLeftTab} onValueChange={setActiveLeftTab} className="flex-1 flex flex-col min-h-0">
        {/* 3-row tab layout — generous gaps + fuchsia active highlight for clarity */}
        <TabsList className="mx-2 mt-2 bg-zinc-900/60 grid grid-cols-3 gap-2 h-auto p-2 flex-shrink-0 rounded-lg border border-zinc-800">
          <TabsTrigger
            value="projectors"
            data-testid="projectors-tab"
            className="text-xs px-2 py-2 rounded-md bg-zinc-800/70 text-zinc-300 hover:bg-zinc-700/70 data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-[0_0_0_1px_rgba(217,70,239,0.6),0_4px_14px_-4px_rgba(217,70,239,0.5)]"
          >
            Projectors
          </TabsTrigger>
          <TabsTrigger
            value="truss"
            data-testid="truss-tab"
            className="text-xs px-2 py-2 rounded-md bg-zinc-800/70 text-zinc-300 hover:bg-zinc-700/70 data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-[0_0_0_1px_rgba(217,70,239,0.6),0_4px_14px_-4px_rgba(217,70,239,0.5)]"
          >
            Truss & Scaffold
          </TabsTrigger>
          <TabsTrigger
            value="led"
            data-testid="led-tab"
            className="text-xs px-2 py-2 rounded-md bg-zinc-800/70 text-zinc-300 hover:bg-zinc-700/70 data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-[0_0_0_1px_rgba(217,70,239,0.6),0_4px_14px_-4px_rgba(217,70,239,0.5)]"
          >
            LED
          </TabsTrigger>
          <TabsTrigger
            value="primitives"
            data-testid="primitives-tab"
            className="text-xs px-2 py-2 rounded-md bg-zinc-800/70 text-zinc-300 hover:bg-zinc-700/70 data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-[0_0_0_1px_rgba(217,70,239,0.6),0_4px_14px_-4px_rgba(217,70,239,0.5)]"
          >
            Objects
          </TabsTrigger>
          <TabsTrigger
            value="files"
            className="text-xs px-2 py-2 rounded-md bg-zinc-800/70 text-zinc-300 hover:bg-zinc-700/70 data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-[0_0_0_1px_rgba(217,70,239,0.6),0_4px_14px_-4px_rgba(217,70,239,0.5)]"
          >
            Files
          </TabsTrigger>
          <TabsTrigger
            value="content"
            className="text-xs px-2 py-2 rounded-md bg-zinc-800/70 text-zinc-300 hover:bg-zinc-700/70 data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-[0_0_0_1px_rgba(217,70,239,0.6),0_4px_14px_-4px_rgba(217,70,239,0.5)]"
          >
            Content
          </TabsTrigger>
          <TabsTrigger
            value="led_test"
            data-testid="led-test-tab"
            className="text-xs px-2 py-2 col-span-3 rounded-md bg-zinc-800/70 text-zinc-300 hover:bg-zinc-700/70 data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-[0_0_0_1px_rgba(217,70,239,0.6),0_4px_14px_-4px_rgba(217,70,239,0.5)]"
          >
            LED Test Pattern
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="projectors" className="flex-1 m-0 overflow-y-auto">
          <div className="p-3 space-y-4">
            {/* Search bar — filters across brand, model, name, id, and lens
                model numbers. Case-insensitive token match (every search
                token must appear in at least one searchable field).
                Sticky to the top of the scroll container so it stays visible
                while users scroll through long brand lists. */}
            <div className="sticky -top-3 -mx-3 px-3 py-2 z-10 bg-zinc-900/95 backdrop-blur-sm border-b border-zinc-800">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
                <Input
                  value={projectorSearch}
                  onChange={(e) => setProjectorSearch(e.target.value)}
                  placeholder="Search projectors (brand, model, lens…)"
                  className="pl-7 pr-7 h-8 text-xs bg-zinc-800 border-zinc-700"
                  data-testid="projector-search-input"
                />
                {projectorSearch && (
                  <button
                    type="button"
                    onClick={() => setProjectorSearch('')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
                    data-testid="projector-search-clear"
                    aria-label="Clear search"
                  >
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {(() => {
              // Build the filtered slices once per render; each spec is
              // matched by ALL whitespace-separated tokens from the query.
              const tokens = projectorSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
              const matches = (spec) => {
                if (tokens.length === 0) return true;
                const haystack = [
                  spec.brand, spec.model, spec.name, spec.id, spec.aspect_ratio,
                  spec.brightness ? `${spec.brightness}` : '',
                  spec.brightness ? `${spec.brightness}lm` : '',
                  ...(Array.isArray(spec.lenses)
                    ? spec.lenses.flatMap((l) => [l.name, l.model, l.id])
                    : []),
                ].filter(Boolean).join(' ').toLowerCase();
                return tokens.every((t) => haystack.includes(t));
              };

              // Brand groups come straight from the backend (Barco, Panasonic,
              // Christie, Epson, Sharp/NEC, Optoma, BenQ, Digital Projection,
              // …); we render them in a stable A→Z order with Barco pinned
              // first as the historical default.
              const brandsObj = projectorLibrary.brands || {};
              const brandEntries = Object.entries(brandsObj)
                .map(([brand, specs]) => [brand, (specs || []).filter(matches)])
                .sort(([a], [b]) => {
                  if (a === 'Barco') return -1;
                  if (b === 'Barco') return 1;
                  if (a === 'Panasonic') return -1;
                  if (b === 'Panasonic') return 1;
                  return a.localeCompare(b);
                });
              const customMatches = (projectorLibrary.custom || []).filter(matches);
              const totalMatches = brandEntries.reduce((s, [, list]) => s + list.length, 0) + customMatches.length;
              const isSearching = tokens.length > 0;

              return (
                <>
                  {isSearching && (
                    <div className="text-[10px] text-zinc-500 -mt-2" data-testid="projector-search-results-count">
                      {totalMatches === 0
                        ? 'No projectors match this search.'
                        : `${totalMatches} match${totalMatches !== 1 ? 'es' : ''}`}
                    </div>
                  )}

                  {/* Render every brand group dynamically (sourced from the
                      backend Excel-driven projector library). */}
                  {brandEntries.map(([brand, specs]) => (
                    specs.length > 0 && (
                      <div key={brand} data-testid={`projector-brand-group-${brand.replace(/\W+/g, '-').toLowerCase()}`}>
                        <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                          {`${brand} Projectors`}
                        </h4>
                        <div className="space-y-2">
                          {specs.map(spec => (
                            <ProjectorCard key={spec.id} spec={spec} category={brand.toLowerCase()} />
                          ))}
                        </div>
                      </div>
                    )
                  ))}

                  {/* Custom Projectors */}
                  {customMatches.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
                        Custom Projectors
                      </h4>
                      <div className="space-y-2">
                        {customMatches.map(spec => (
                          <ProjectorCard key={spec.id} spec={spec} category="custom" />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Add Custom Projector Dialog */}
            <Dialog open={customProjectorOpen} onOpenChange={setCustomProjectorOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full text-xs border-dashed border-zinc-600 hover:border-fuchsia-500"
                  data-testid="add-custom-projector-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Custom Projector
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Custom Projector</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Projector Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Brand</Label>
                      <Input
                        value={customProjector.brand}
                        onChange={(e) => setCustomProjector({ ...customProjector, brand: e.target.value })}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Model No *</Label>
                      <Input
                        value={customProjector.model}
                        onChange={(e) => setCustomProjector({ ...customProjector, model: e.target.value })}
                        className="h-8 text-xs"
                        placeholder="Required"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Lumens</Label>
                      <Input
                        type="number"
                        value={customProjector.brightness}
                        onChange={(e) => setCustomProjector({ ...customProjector, brightness: parseInt(e.target.value) || 0 })}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Projector Weight (kg)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={customProjector.weight}
                        onChange={(e) => setCustomProjector({ ...customProjector, weight: parseFloat(e.target.value) || 0 })}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label className="text-[10px] text-zinc-500">Width (mm)</Label>
                      <Input
                        type="number"
                        value={customProjector.width}
                        onChange={(e) => setCustomProjector({ ...customProjector, width: parseInt(e.target.value) || 0 })}
                        className="h-7 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-zinc-500">Depth (mm)</Label>
                      <Input
                        type="number"
                        value={customProjector.depth}
                        onChange={(e) => setCustomProjector({ ...customProjector, depth: parseInt(e.target.value) || 0 })}
                        className="h-7 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-zinc-500">Height (mm)</Label>
                      <Input
                        type="number"
                        value={customProjector.height}
                        onChange={(e) => setCustomProjector({ ...customProjector, height: parseInt(e.target.value) || 0 })}
                        className="h-7 text-xs"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Aspect Ratio</Label>
                      <Select
                        value={customProjector.aspect_ratio}
                        onValueChange={(val) => setCustomProjector({ ...customProjector, aspect_ratio: val })}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="16:10">16:10</SelectItem>
                          <SelectItem value="16:9">16:9</SelectItem>
                          <SelectItem value="4:3">4:3</SelectItem>
                          <SelectItem value="21:9">21:9</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Resolution Width</Label>
                      <Input
                        type="number"
                        value={customProjector.resolution_width}
                        onChange={(e) => setCustomProjector({ ...customProjector, resolution_width: parseInt(e.target.value) || 1920 })}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Resolution Height</Label>
                      <Input
                        type="number"
                        value={customProjector.resolution_height}
                        onChange={(e) => setCustomProjector({ ...customProjector, resolution_height: parseInt(e.target.value) || 1080 })}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                  
                  {/* Frame Section */}
                  <div className="border-t border-zinc-700 pt-3">
                    <Label className="text-xs text-zinc-400 mb-2 block">Frame Dimensions</Label>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-zinc-500">Width (mm)</Label>
                        <Input
                          type="number"
                          value={customProjector.frame_width}
                          onChange={(e) => setCustomProjector({ ...customProjector, frame_width: parseInt(e.target.value) || 0 })}
                          className="h-7 text-xs"
                          data-testid="custom-proj-frame-width"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-zinc-500">Depth (mm)</Label>
                        <Input
                          type="number"
                          value={customProjector.frame_depth}
                          onChange={(e) => setCustomProjector({ ...customProjector, frame_depth: parseInt(e.target.value) || 0 })}
                          className="h-7 text-xs"
                          data-testid="custom-proj-frame-depth"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-zinc-500">Height (mm)</Label>
                        <Input
                          type="number"
                          value={customProjector.frame_height}
                          onChange={(e) => setCustomProjector({ ...customProjector, frame_height: parseInt(e.target.value) || 0 })}
                          className="h-7 text-xs"
                          data-testid="custom-proj-frame-height"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-zinc-500">Weight (kg)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={customProjector.frame_weight}
                          onChange={(e) => setCustomProjector({ ...customProjector, frame_weight: parseFloat(e.target.value) || 0 })}
                          className="h-7 text-xs"
                          data-testid="custom-proj-frame-weight"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Lens Defaults */}
                  <div className="border-t border-zinc-700 pt-3">
                    <Label className="text-xs text-zinc-400 mb-2 block">Default Lens</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-zinc-500">Throw Ratio Min</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={customProjector.throw_ratio_min}
                          onChange={(e) => setCustomProjector({ ...customProjector, throw_ratio_min: parseFloat(e.target.value) || 1.0 })}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-zinc-500">Throw Ratio Max</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={customProjector.throw_ratio_max}
                          onChange={(e) => setCustomProjector({ ...customProjector, throw_ratio_max: parseFloat(e.target.value) || 2.0 })}
                          className="h-7 text-xs"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-zinc-500">Lens Shift H (%)</Label>
                        <Input
                          type="number"
                          value={customProjector.lens_shift_h}
                          onChange={(e) => setCustomProjector({ ...customProjector, lens_shift_h: parseInt(e.target.value) || 0 })}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-zinc-500">Lens Shift V (%)</Label>
                        <Input
                          type="number"
                          value={customProjector.lens_shift_v}
                          onChange={(e) => setCustomProjector({ ...customProjector, lens_shift_v: parseInt(e.target.value) || 0 })}
                          className="h-7 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    className="w-full" 
                    onClick={handleAddCustomProjector}
                  >
                    Create & Add to Scene
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>

        <TabsContent value="truss" className="flex-1 m-0 overflow-y-auto">
          <div className="p-3 space-y-3">
            <div className="text-[10px] text-zinc-500 text-center mb-2">
              Based on Global Truss specifications (F34/F33/F32)
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {trussStructures.map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.structureType}
                    variant="outline"
                    className="h-24 flex-col gap-1 border-zinc-700 hover:border-cyan-500 hover:bg-zinc-800"
                    onClick={() => handleAddTruss(item)}
                    data-testid={`add-truss-${item.structureType}-btn`}
                  >
                    <Icon className="w-6 h-6 text-cyan-400" />
                    <span className="text-xs font-medium">{item.label}</span>
                    <span className="text-[9px] text-zinc-500">{item.description}</span>
                  </Button>
                );
              })}
            </div>

            <div className="border-t border-zinc-700 pt-3 space-y-2">
              <h4 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Quick Add Segments</h4>
              <div className="grid grid-cols-4 gap-1">
                {[1, 2, 3, 4].map(len => (
                  <Button
                    key={len}
                    variant="outline"
                    size="sm"
                    className="h-10 flex-col gap-0.5 border-zinc-700 hover:border-cyan-500 text-xs"
                    onClick={() => handleAddTruss({
                      label: `Truss ${len}m`,
                      structureType: 'straight',
                      defaultConfig: { structureType: 'straight', trussProfile: 'F34', length: len, color: '#C0C0C0', showBasePlates: false }
                    })}
                    data-testid={`add-truss-${len}m-btn`}
                  >
                    <span className="text-xs font-bold text-cyan-300">{len}m</span>
                    <span className="text-[8px] text-zinc-500">F34</span>
                  </Button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-1">
                {[
                  { key: 'F34', label: 'F34 Box', profile: 'F34' },
                  { key: 'F33', label: 'F33 Tri', profile: 'F33' },
                  { key: 'F32', label: 'F32 Ladder', profile: 'F32' },
                ].map(item => (
                  <Button
                    key={item.key}
                    variant="outline"
                    size="sm"
                    className="h-10 flex-col gap-0.5 border-zinc-700 hover:border-cyan-500 text-xs"
                    onClick={() => handleAddTruss({
                      label: `${item.label} 3m`,
                      structureType: 'straight',
                      defaultConfig: { structureType: 'straight', trussProfile: item.profile, length: 3, color: '#C0C0C0', showBasePlates: false }
                    })}
                    data-testid={`add-truss-${item.key.toLowerCase()}-3m-btn`}
                  >
                    <span className="text-[10px] font-medium text-cyan-300">{item.label}</span>
                    <span className="text-[8px] text-zinc-500">3m segment</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* ── SCAFFOLDING (moved here from Objects tab per user request) ── */}
            <div className="border-t border-zinc-700 pt-3 space-y-2">
              <h4 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Scaffolding</h4>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="h-24 flex-col gap-1 border-zinc-700 hover:border-cyan-500 hover:bg-zinc-800"
                  onClick={() => handleAddPrimitive('scaffolding')}
                  data-testid="add-scaffolding-btn"
                >
                  <Box className="w-6 h-6 text-cyan-400" />
                  <span className="text-xs font-medium">Scaffolding</span>
                  <span className="text-[9px] text-zinc-500">Modular ringlock scaffold</span>
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="led" className="flex-1 m-0 overflow-y-auto" data-testid="led-tab-content">
          <div className="p-3 space-y-3">
            <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
              LED Screens
            </h4>
            <p className="text-[10px] text-zinc-500 -mt-2">
              All LED panel variants. Click to add to scene.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {ledScreens.map((prim, idx) => {
                const { type, icon: Icon, label, description } = prim;
                return (
                  <Button
                    key={`${type}-${label}-${idx}`}
                    variant="outline"
                    className="h-24 flex-col gap-1 border-zinc-700 hover:border-fuchsia-500 hover:bg-zinc-800"
                    onClick={() => handleAddPrimitive(type)}
                    data-testid={`add-${type}-${label.toLowerCase().replace(/\s/g, '-')}-btn`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-xs font-medium">{label}</span>
                    {description && <span className="text-[9px] text-zinc-500">{description}</span>}
                  </Button>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="primitives" className="flex-1 m-0 overflow-y-auto">
          <div className="p-3">
            <div className="grid grid-cols-2 gap-2">
              {primitives.map((prim, idx) => {
                const { type, icon: Icon, label, description, trussLength } = prim;
                return (
                  <Button
                    key={`${type}-${label}-${idx}`}
                    variant="outline"
                    className="h-24 flex-col gap-1 border-zinc-700 hover:border-fuchsia-500 hover:bg-zinc-800"
                    onClick={() => handleAddPrimitive(type)}
                    data-testid={`add-${type}-${label.toLowerCase().replace(/\s/g, '-')}-btn`}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-xs font-medium">{label}</span>
                    {description && <span className="text-[9px] text-zinc-500">{description}</span>}
                  </Button>
                );
              })}
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="files" className="flex-1 m-0 overflow-y-auto">
          <div className="p-3 space-y-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".obj,.fbx,.gltf,.glb,.mtl"
              className="hidden"
              disabled={isUploading}
            />
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1 text-xs border-dashed border-zinc-600 hover:border-fuchsia-500"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-file-btn"
                disabled={isUploading || isConverting}
              >
                <Upload className="w-4 h-4 mr-2" />
                {isUploading ? 'Uploading...' : 'Upload 3D File'}
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 border-zinc-600 hover:border-fuchsia-500"
                onClick={() => {
                  setNewFolderName('');
                  setNewFolderColor(FOLDER_COLOR_SWATCHES[0]);
                  setNewFolderDialogOpen(true);
                }}
                title="New folder"
                data-testid="new-folder-btn"
              >
                <FolderPlus className="w-4 h-4" />
              </Button>
            </div>

            {/* ── Upload & Convert (always outputs optimized GLB) ── */}
            <input
              ref={convertInputRef}
              type="file"
              onChange={handleConvertUpload}
              accept=".obj,.fbx,.gltf,.glb"
              className="hidden"
              disabled={isConverting || isUploading}
              data-testid="upload-convert-input"
            />
            <Button
              variant="outline"
              className="w-full text-xs border-dashed border-cyan-500/50 hover:border-cyan-400 hover:bg-cyan-500/5 text-cyan-200"
              onClick={() => convertInputRef.current?.click()}
              disabled={isConverting || isUploading}
              data-testid="upload-convert-btn"
              title="Loads OBJ/FBX/GLTF/GLB → exports optimized GLB → captures thumbnail"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {isConverting ? (convertLabel || 'Converting…') : 'Upload & Convert (GLB)'}
            </Button>

            {/* Conversion Progress Bar */}
            {isConverting && (
              <div className="space-y-1">
                <div className="flex justify-between text-[10px] text-cyan-300">
                  <span>{convertLabel || 'Working…'}</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Upload Progress Bar */}
            {isUploading && (
              <div className="space-y-1">
                <div className="flex justify-between text-[10px] text-zinc-400">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-fuchsia-500 to-blue-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
            
            <div className="text-xs text-zinc-500 text-center">
              Supports: OBJ, GLB, GLTF (Max: 200MB)
            </div>
            <div className="text-[10px] text-cyan-400/80 text-center leading-tight">
              <span className="font-semibold">Upload &amp; Convert</span>: auto-converts OBJ/FBX → optimized GLB
            </div>
            <div className="text-[10px] text-yellow-500/80 text-center">
              Tip: For complex FBX, pre-convert with Blender for best results
            </div>
            
            <div className="text-[10px] text-fuchsia-400 text-center">
              Tip: Drag &amp; drop files directly into the 3D scene
            </div>

            {/* Folder strip (Root + user folders) */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wide text-zinc-500">Folders</span>
                {activeFolderId && (
                  <button
                    className="text-[10px] text-fuchsia-400 hover:text-fuchsia-300"
                    onClick={() => setActiveFolderId(null)}
                    data-testid="clear-folder-filter-btn"
                  >
                    Clear filter
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setActiveFolderId(null)}
                  data-testid="folder-chip-root"
                  className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] border transition-colors ${
                    !activeFolderId
                      ? 'bg-zinc-700 border-zinc-500 text-zinc-100'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                  }`}
                >
                  <Home className="w-3 h-3" />
                  All
                </button>
                {modelFolders.map(folder => {
                  const fileCount = uploadedFiles.filter(f => f.folder_id === folder.id).length;
                  const selected = activeFolderId === folder.id;
                  return (
                    <div
                      key={folder.id}
                      className={`group flex items-center gap-1 pl-2 pr-0.5 py-1 rounded text-[11px] border transition-colors ${
                        selected
                          ? 'bg-zinc-700 border-zinc-500 text-zinc-100'
                          : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                      }`}
                      data-testid={`folder-chip-${folder.id}`}
                    >
                      <button
                        onClick={() => setActiveFolderId(folder.id)}
                        className="flex items-center gap-1"
                      >
                        <Folder className="w-3 h-3" style={{ color: folder.color }} fill={folder.color} />
                        <span className="max-w-[90px] truncate">{folder.name}</span>
                        <span className="text-zinc-500">({fileCount})</span>
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`folder-menu-${folder.id}`}
                          >
                            <MoreVertical className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); openEditFolder(folder); }}
                            className="text-xs"
                            data-testid={`folder-rename-${folder.id}`}
                          >
                            <Edit className="w-3 h-3 mr-2" />
                            Rename / Recolor
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); openDeleteFolder(folder); }}
                            className="text-xs text-red-400"
                            data-testid={`folder-delete-${folder.id}`}
                          >
                            <Trash2 className="w-3 h-3 mr-2" />
                            Delete folder
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              {uploadedFiles
                .filter(f => activeFolderId ? f.folder_id === activeFolderId : true)
                .map(file => {
                  const parentFolder = file.folder_id ? modelFolders.find(mf => mf.id === file.folder_id) : null;
                  return (
                <div 
                  key={file.id}
                  className="flex items-center gap-2 p-2 bg-zinc-800 rounded border border-zinc-700 hover:border-zinc-600 cursor-pointer group"
                  onClick={() => handleAddMeshToScene(file)}
                  data-testid={`file-${file.id}`}
                >
                  <FileIcon className="w-4 h-4 text-zinc-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{file.name}</div>
                    <div className="text-[10px] text-zinc-500 uppercase flex items-center gap-1">
                      <span>{file.file_type}</span>
                      {file.is_edited && <span className="text-blue-400 normal-case">(Edited)</span>}
                      {parentFolder && !activeFolderId && (
                        <span className="flex items-center gap-0.5 normal-case text-zinc-400">
                          <Folder className="w-2.5 h-2.5" style={{ color: parentFolder.color }} fill={parentFolder.color} />
                          {parentFolder.name}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Actions dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`file-menu-${file.id}`}
                      >
                        <MoreVertical className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-zinc-500">
                        Move to
                      </div>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveFileToFolder(file.id, null);
                        }}
                        className="text-xs"
                        disabled={!file.folder_id}
                        data-testid={`file-move-root-${file.id}`}
                      >
                        <Home className="w-3 h-3 mr-2" />
                        Root
                        {!file.folder_id && <Check className="w-3 h-3 ml-auto" />}
                      </DropdownMenuItem>
                      {modelFolders.map(folder => (
                        <DropdownMenuItem
                          key={folder.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMoveFileToFolder(file.id, folder.id);
                          }}
                          className="text-xs"
                          disabled={file.folder_id === folder.id}
                          data-testid={`file-move-${folder.id}-${file.id}`}
                        >
                          <Folder className="w-3 h-3 mr-2" style={{ color: folder.color }} fill={folder.color} />
                          <span className="truncate max-w-[120px]">{folder.name}</span>
                          {file.folder_id === folder.id && <Check className="w-3 h-3 ml-auto" />}
                        </DropdownMenuItem>
                      ))}
                      {modelFolders.length === 0 && (
                        <div className="px-2 py-1 text-[10px] text-zinc-500">No folders yet</div>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          openExportDialog(file);
                        }}
                        className="text-xs"
                      >
                        <Download className="w-3 h-3 mr-2" />
                        Export / Download
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileDelete(file.id);
                        }}
                        className="text-xs text-red-400"
                      >
                        <Trash2 className="w-3 h-3 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                  );
                })}
              
              {uploadedFiles.length === 0 && (
                <div className="text-center text-zinc-500 text-xs py-4">
                  No files uploaded yet
                </div>
              )}
              {uploadedFiles.length > 0 &&
                uploadedFiles.filter(f => activeFolderId ? f.folder_id === activeFolderId : true).length === 0 && (
                <div className="text-center text-zinc-500 text-xs py-4">
                  This folder is empty
                </div>
              )}
            </div>
          </div>
        </TabsContent>
        
        {/* Content Tab */}
        <TabsContent value="content" className="flex-1 m-0 overflow-hidden">
          <ContentPanel />
        </TabsContent>

        {/* LED Test Pattern Tab */}
        <TabsContent value="led_test" className="flex-1 m-0 overflow-hidden">
          <LedTestPattern />
        </TabsContent>
      </Tabs>
      
      {/* Export File Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Export 3D Model</DialogTitle>
          </DialogHeader>
          
          {exportingFile && (
            <div className="space-y-4 py-4">
              <div className="text-sm text-zinc-300">
                File: <span className="text-zinc-100 font-medium">{exportingFile.name}</span>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-400">Export Format</Label>
                <Select value={exportFormat} onValueChange={setExportFormat}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700">
                    <SelectItem value="obj">OBJ (Wavefront)</SelectItem>
                    <SelectItem value="fbx">FBX (Autodesk)</SelectItem>
                    <SelectItem value="gltf">GLTF (Standard)</SelectItem>
                    <SelectItem value="glb">GLB (Binary GLTF)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-zinc-500">
                  Note: Format conversion may take a moment
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExportDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExportFile}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Projector Dialog */}
      <Dialog open={editProjectorOpen} onOpenChange={setEditProjectorOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Projector</DialogTitle>
          </DialogHeader>
          
          {editingSpec && (
            <div className="space-y-4">
              {/* Projector Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Brand</Label>
                  <Input
                    value={editingSpec.brand}
                    onChange={(e) => setEditingSpec({ ...editingSpec, brand: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Model No</Label>
                  <Input
                    value={editingSpec.model}
                    onChange={(e) => setEditingSpec({ ...editingSpec, model: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Lumens</Label>
                  <Input
                    type="number"
                    value={editingSpec.brightness}
                    onChange={(e) => setEditingSpec({ ...editingSpec, brightness: parseInt(e.target.value) || 0 })}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Projector Weight (kg)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editingSpec.weight}
                    onChange={(e) => setEditingSpec({ ...editingSpec, weight: parseFloat(e.target.value) || 0 })}
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-[10px] text-zinc-500">Width (mm)</Label>
                  <Input
                    type="number"
                    value={editingSpec.width}
                    onChange={(e) => setEditingSpec({ ...editingSpec, width: parseInt(e.target.value) || 0 })}
                    className="h-7 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-zinc-500">Depth (mm)</Label>
                  <Input
                    type="number"
                    value={editingSpec.depth}
                    onChange={(e) => setEditingSpec({ ...editingSpec, depth: parseInt(e.target.value) || 0 })}
                    className="h-7 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-zinc-500">Height (mm)</Label>
                  <Input
                    type="number"
                    value={editingSpec.height}
                    onChange={(e) => setEditingSpec({ ...editingSpec, height: parseInt(e.target.value) || 0 })}
                    className="h-7 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Aspect Ratio</Label>
                  <Select
                    value={editingSpec.aspect_ratio}
                    onValueChange={(val) => setEditingSpec({ ...editingSpec, aspect_ratio: val })}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="16:10">16:10</SelectItem>
                      <SelectItem value="16:9">16:9</SelectItem>
                      <SelectItem value="4:3">4:3</SelectItem>
                      <SelectItem value="21:9">21:9</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Resolution Width</Label>
                  <Input
                    type="number"
                    value={editingSpec.resolution_width || 1920}
                    onChange={(e) => setEditingSpec({ ...editingSpec, resolution_width: parseInt(e.target.value) || 1920 })}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Resolution Height</Label>
                  <Input
                    type="number"
                    value={editingSpec.resolution_height || 1200}
                    onChange={(e) => setEditingSpec({ ...editingSpec, resolution_height: parseInt(e.target.value) || 1080 })}
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              {/* Frame Section */}
              <div className="border-t border-zinc-700 pt-3">
                <Label className="text-xs text-zinc-400 mb-2 block">Frame Dimensions</Label>
                <div className="grid grid-cols-4 gap-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-zinc-500">Width (mm)</Label>
                    <Input
                      type="number"
                      value={editingSpec.frame_width || 0}
                      onChange={(e) => setEditingSpec({ ...editingSpec, frame_width: parseInt(e.target.value) || 0 })}
                      className="h-7 text-xs"
                      data-testid="edit-proj-frame-width"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-zinc-500">Depth (mm)</Label>
                    <Input
                      type="number"
                      value={editingSpec.frame_depth || 0}
                      onChange={(e) => setEditingSpec({ ...editingSpec, frame_depth: parseInt(e.target.value) || 0 })}
                      className="h-7 text-xs"
                      data-testid="edit-proj-frame-depth"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-zinc-500">Height (mm)</Label>
                    <Input
                      type="number"
                      value={editingSpec.frame_height || 0}
                      onChange={(e) => setEditingSpec({ ...editingSpec, frame_height: parseInt(e.target.value) || 0 })}
                      className="h-7 text-xs"
                      data-testid="edit-proj-frame-height"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-zinc-500">Weight (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editingSpec.frame_weight || 0}
                      onChange={(e) => setEditingSpec({ ...editingSpec, frame_weight: parseFloat(e.target.value) || 0 })}
                      className="h-7 text-xs"
                      data-testid="edit-proj-frame-weight"
                    />
                  </div>
                </div>
              </div>

              {/* Lens Defaults */}
              <div className="border-t border-zinc-700 pt-3">
                <Label className="text-xs text-zinc-400 mb-2 block">Default Lens</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-zinc-500">Throw Ratio Min</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editingSpec.throw_ratio_min}
                      onChange={(e) => setEditingSpec({ ...editingSpec, throw_ratio_min: parseFloat(e.target.value) || 0 })}
                      className="h-7 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-zinc-500">Throw Ratio Max</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={editingSpec.throw_ratio_max}
                      onChange={(e) => setEditingSpec({ ...editingSpec, throw_ratio_max: parseFloat(e.target.value) || 0 })}
                      className="h-7 text-xs"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="space-y-1">
                    <Label className="text-[10px] text-zinc-500">Lens Shift H (%)</Label>
                    <Input
                      type="number"
                      value={editingSpec.lens_shift_h || 0}
                      onChange={(e) => setEditingSpec({ ...editingSpec, lens_shift_h: parseInt(e.target.value) || 0 })}
                      className="h-7 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] text-zinc-500">Lens Shift V (%)</Label>
                    <Input
                      type="number"
                      value={editingSpec.lens_shift_v || 0}
                      onChange={(e) => setEditingSpec({ ...editingSpec, lens_shift_v: parseInt(e.target.value) || 0 })}
                      className="h-7 text-xs"
                    />
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditProjectorOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEditedProjector}>
                  Save Changes
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={newFolderDialogOpen} onOpenChange={setNewFolderDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-sm" data-testid="new-folder-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">New 3D Model Folder</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Create a color-tagged folder to organize your uploaded 3D files.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Name</Label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g. Trusses, Lighting, Props"
                className="h-8 text-xs bg-zinc-800 border-zinc-700"
                autoFocus
                data-testid="new-folder-name-input"
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); }}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs flex items-center gap-1">
                <Palette className="w-3 h-3" /> Color
              </Label>
              <div className="flex flex-wrap gap-2">
                {FOLDER_COLOR_SWATCHES.map(c => (
                  <button
                    key={c}
                    onClick={() => setNewFolderColor(c)}
                    data-testid={`new-folder-color-${c}`}
                    className={`w-7 h-7 rounded-md border-2 transition-transform ${newFolderColor === c ? 'border-white scale-110' : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: c }}
                    aria-label={`Color ${c}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder} data-testid="new-folder-create-btn">
              <FolderPlus className="w-4 h-4 mr-1" /> Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog open={editFolderDialogOpen} onOpenChange={setEditFolderDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-sm" data-testid="edit-folder-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Edit Folder</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Rename the folder or change its color tag.
            </DialogDescription>
          </DialogHeader>
          {editingFolder && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs">Name</Label>
                <Input
                  value={editingFolder.name}
                  onChange={(e) => setEditingFolder({ ...editingFolder, name: e.target.value })}
                  className="h-8 text-xs bg-zinc-800 border-zinc-700"
                  autoFocus
                  data-testid="edit-folder-name-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveFolderEdit(); }}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400 text-xs flex items-center gap-1">
                  <Palette className="w-3 h-3" /> Color
                </Label>
                <div className="flex flex-wrap gap-2">
                  {FOLDER_COLOR_SWATCHES.map(c => (
                    <button
                      key={c}
                      onClick={() => setEditingFolder({ ...editingFolder, color: c })}
                      data-testid={`edit-folder-color-${c}`}
                      className={`w-7 h-7 rounded-md border-2 transition-transform ${editingFolder.color === c ? 'border-white scale-110' : 'border-transparent hover:scale-105'}`}
                      style={{ backgroundColor: c }}
                      aria-label={`Color ${c}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFolderDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveFolderEdit} data-testid="edit-folder-save-btn">
              <Check className="w-4 h-4 mr-1" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Confirmation Dialog */}
      <Dialog open={deleteFolderDialogOpen} onOpenChange={setDeleteFolderDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-sm" data-testid="delete-folder-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Delete Folder?</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Remove this folder. All 3D files inside will be deleted permanently.
            </DialogDescription>
          </DialogHeader>
          {deletingFolder && (
            <div className="space-y-3 py-2 text-xs text-zinc-300">
              <p>
                You are about to delete the folder{' '}
                <span className="font-semibold text-zinc-100">"{deletingFolder.name}"</span>.
              </p>
              {(() => {
                const count = uploadedFiles.filter(f => f.folder_id === deletingFolder.id).length;
                return (
                  <p className={count > 0 ? 'text-red-400' : 'text-zinc-400'}>
                    {count > 0
                      ? `All ${count} file${count === 1 ? '' : 's'} inside will also be permanently deleted.`
                      : 'This folder is empty.'}
                  </p>
                );
              })()}
              <p className="text-zinc-500">This action cannot be undone.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFolderDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleDeleteFolder}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-folder-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LibraryPanel;
